/* eslint-disable max-lines */
import { useState, useRef, useEffect, useContext, createRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { ThemeContext } from 'styled-components';
import Paper from '@base/Paper/Paper';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import Cookies from '@utils/cookies';
import { useDispatchToStore, useSelectorToStore } from '@hooks/reduxHooks';
import { getGoalLanguages, setGoalLanguage } from '@actions/topologies';
import {
  setSelectedGoal as createSetSelectedGoalAction,
  followGoal,
  fetchFollowedGoals
} from '@actions/auth';
import pushRoute from '@utils/pushRoute';
import Me from '@models/Me';
import { segment } from '@actions/analytics';
import {
  ONBOARDING_LANGUAGE_SELECTED,
  ONBOARDING_GOAL_SELECTED
} from '@constants/segment';
import Dynamic from '@base/Dynamic/Dynamic';
import { Spacings } from '@styles/index';
import { goalLevelOnboardingStatus } from '@constants/index';
import { Topology } from '@models/Topology';
import { getCurrentPageName } from '@selectors/index';
import GoalCard from './GoalCard';
import GoalGroupNav from './GoalGroupNav';
import ChooseGoal from './ChooseGoal';

const Dialog = Dynamic(() => import('@comp/Dialog/Dialog'));
const LanguageSelectionDrawer = Dynamic(() =>
  import('@comp/LanguageSelectionDrawer/LanguageSelectionDrawer')
);

const Container = styled.div`
  width: 1136px;
  margin: 40px auto 0 auto;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  gap: 16px;
  min-height: calc(100vh - 384px);
  @media (max-width: 70.95em) {
    width: 728px;
    margin-top: 24px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0;
    background-color: #fff;
    margin-top: 0;
  }
`;

const PageHeader = styled(Paper)`
  grid-column: span 24;
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_10B};
  @media (max-width: 70.95em) {
    margin-top: 0;
  }
  @media (max-width: 47.95em) {
    margin-bottom: 0;
    box-shadow: none;
    padding: 24px 16px 16px;
    position: fixed;
    width: 100%;
    z-index: 3;
    display: ${({ $hide }) => ($hide ? 'none' : 'block')};
    background: #fff;
  }
`;

const GoalGroupContainer = styled.div`
  margin-bottom: ${Spacings.SPACING_12B};
`;

const GoalItems = styled.div`
  grid-column: span 18;
  padding-bottom: 350px;
  @media (max-width: 70.95em) {
    padding-bottom: 175px;
    grid-column: 9/25;
  }
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const GoalGroupChildren = styled.div`
  display: grid;
  grid-template-columns: repeat(18, 32px);
  grid-column-gap: 16px;
  @media (max-width: 70.95em) {
    grid-template-columns: repeat(18, 1fr);
  }
`;

const Title = styled(H3)`
  display: none;
  @media (max-width: 47.95em) {
    display: block;
    padding: 0 16px;
    margin: 24px 0 0;
  }
`;

const FollowedGoals = styled.div`
  display: none;
  @media (max-width: 47.95em) {
    display: block;
    padding: 0 16px;
    margin-bottom: 32px;
  }
`;

const getNewGoal = (goal) => (goal.relative_link ? new Topology(goal) : goal);

const showError = ({ dispatch, res }) => {
  dispatch({
    type: 'SET_SNACKBAR_MESSAGE',
    message:
      res.error.message ||
      'Some error occurred. Please refresh the page and try again.',
    messageType: 'error'
  });
};

const ManageGoals = ({
  goalGroupsData,
  followedGoals,
  isOnboarding,
  goalLanguages
}) => {
  const [drawerState, setdrawerState] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [chooseGoal, setChooseGoal] = useState(null);
  const [goalToBeRemoved, setGoalToBeRemoved] = useState(null);
  const [currentRefIndex, setCurrentRefIndex] = useState(0);
  const isMobile = useSelector(({ helper }) => helper.isMobile);
  const theme = useContext(ThemeContext);
  const dispatch = useDispatchToStore();

  const isWindowDefined = typeof window !== 'undefined';
  let lastScrollTop = isWindowDefined && window.pageYOffset;

  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn || false);
  const me = useSelectorToStore(({ auth }) => auth.me) || {};
  const authSelectedGoalUid = useSelector(
    ({ auth }) => auth.selectedGoal?.uid || ''
  );

  const goalGroups =
    followedGoals.length > 0
      ? [
          {
            name: 'My goals',
            uid: 'my-goals',
            children: followedGoals
          },
          ...goalGroupsData
        ]
      : goalGroupsData;

  const onLanguageSelect = async (languageCode, languageText) => {
    await dispatch(followGoal(selectedGoal.uid, true));
    dispatch(fetchFollowedGoals());
    const setGoalLanguageResponse = await dispatch(
      setGoalLanguage(selectedGoal.uid, languageCode, languageText)
    );
    if (setGoalLanguageResponse) {
      const selectedGoalExtraData = {
        ...selectedGoal,
        relativeLink: selectedGoal.relative_link,
        label: selectedGoal.name,
        value: selectedGoal.uid,
        as: selectedGoal.relative_link,
        href: '/goal/[goalSlug]/[goalUID]'
      };
      Cookies.setCookie('selectedGoalUid', selectedGoal.uid);
      dispatch(createSetSelectedGoalAction(selectedGoalExtraData));
      segment.track(ONBOARDING_LANGUAGE_SELECTED, {
        goal_uid: selectedGoal.uid,
        goal_name: selectedGoal.name,
        language: languageCode
      });
      segment.track(ONBOARDING_GOAL_SELECTED, {
        goal_uid: selectedGoal.uid,
        goal_name: selectedGoal.name,
        language: languageCode
      });
      setdrawerState(false);
      setSelectedGoal(null);
      setChooseGoal(null);
    }
  };

  const screenName = useSelector(getCurrentPageName);

  const gotoSelectedGoal = (goal, routeObj) => {
    const route = routeObj || {
      href: '/goal/[goalSlug]/[goalUID]',
      as: `/goal/${goal.slug}/${goal.uid}`
    };
    Cookies.setCookie('selectedGoalUid', goal.uid);
    dispatch(createSetSelectedGoalAction(getNewGoal(goal)));
    pushRoute(route.href, route.as, null, screenName);
  };

  const goToGloPage = (goal) => {
    gotoSelectedGoal(goal, {
      href: '/goal/[goalSlug]/[goalUID]/preferences',
      as: `/goal/${goal.slug}/${goal.uid}/preferences`
    });
  };

  const addToMyGoals = async (goal, gloData) => {
    if (gloData.isAvailable && isLoggedIn) {
      let res = await dispatch(followGoal(goal.uid, true));
      if (res?.error) {
        showError({ dispatch, res });
        return;
      }
      segment.track(ONBOARDING_GOAL_SELECTED, {
        goal_uid: goal.uid,
        goal_name: goal.name || '',
        language: null
      });
      res = await dispatch(fetchFollowedGoals());
      if (res?.error) {
        showError({ dispatch, res });
        return;
      }
      if (gloData.status !== goalLevelOnboardingStatus.COMPLETE && !isMobile) {
        goToGloPage(goal);
      }
    } else if (isLoggedIn) {
      const res = await dispatch(getGoalLanguages(goal.uid));
      if (res?.error) {
        showError({ dispatch, res });
        return;
      }
      setdrawerState(true);
      setSelectedGoal(goal);
    } else {
      gotoSelectedGoal(goal);
    }
  };

  const removeFromMyGoals = (goal) => {
    setGoalToBeRemoved(goal);
  };

  const onGoalCardClick = ({ isMyGoal, isFollowedGoal, isSubscribedGoal }) => (
    goal
  ) => {
    const gloData = goal.goalLevelOnboarding || {
      isAvailable: goal.is_goal_onboarding_available || false,
      status: goal.user_onboarding_status || -1
    };

    if (isMyGoal || isFollowedGoal || isSubscribedGoal) {
      if (
        gloData.isAvailable &&
        gloData.status !== goalLevelOnboardingStatus.COMPLETE
      )
        goToGloPage(goal);
      else gotoSelectedGoal(goal);
    } else addToMyGoals(goal, gloData);
  };

  const removeGoalCall = () => {
    dispatch(followGoal(goalToBeRemoved.uid, false)).then(async (response) => {
      const removedGoalUid = goalToBeRemoved?.uid;
      setGoalToBeRemoved(null);
      const followedGoalsResp = await dispatch(fetchFollowedGoals());
      if (response && removedGoalUid === authSelectedGoalUid) {
        // Change selected goal
        let goalItem = null;

        if (me.plusSubscriptions?.length > 0) {
          goalItem = me.plusSubscriptions[0].subscription.value;
        } else if (followedGoalsResp?.rawResponse?.length > 0) {
          [goalItem] = followedGoalsResp.rawResponse;
        }

        if (goalItem) {
          const activeGoalUid = goalItem.uid;
          const activeGoal = new Topology({
            ...goalItem,
            slug: goalItem.relative_link.split('/')[2]
          });
          Cookies.setCookie('selectedGoalUid', activeGoalUid);
          dispatch(createSetSelectedGoalAction(activeGoal));
        } else {
          dispatch(createSetSelectedGoalAction({}));
          Cookies.eraseCookie('selectedGoalUid');
        }
      }
    });
  };

  const goalGroupRefs = useRef(goalGroups.map(createRef));

  useEffect(() => {
    document.all[0].style.scrollBehavior = 'smooth';
    return () => {
      document.all[0].style.scrollBehavior = 'unset';
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window?.pageYOffset || document?.documentElement?.scrollTop;
      if (
        currentRefIndex < goalGroupRefs.current.length &&
        scrollTop > lastScrollTop
      ) {
        // down scroll
        const nextRef = goalGroupRefs.current[currentRefIndex + 1];
        if (nextRef?.current.getBoundingClientRect().y <= 200) {
          setCurrentRefIndex(currentRefIndex + 1);
        }
      } else if (currentRefIndex > 0) {
        // up scroll
        const previousRef = goalGroupRefs.current[currentRefIndex - 1];
        const currentRef = goalGroupRefs.current[currentRefIndex];
        if (
          previousRef?.current.getBoundingClientRect().bottom > 200 &&
          currentRef?.current.getBoundingClientRect().y > 300
        ) {
          setCurrentRefIndex(currentRefIndex - 1);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    };
    window.addEventListener('scroll', handleScroll, false);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentRefIndex]);

  const handleChooseGoal = (goal) => {
    setChooseGoal(goal);
  };
  const resetChooseGoal = () => {
    setChooseGoal(null);
  };
  return (
    <Container>
      <PageHeader $hide={followedGoals.length > 0}>
        <H3 color={theme.colors.fontGrey}>
          {isOnboarding ? 'Choose your goal' : 'Manage goals'}
        </H3>
      </PageHeader>
      {isOnboarding || followedGoals.length === 0 ? null : (
        <>
          <Title color={theme.colors.fontGrey}>My goals</Title>
          <FollowedGoals>
            {followedGoals.map((goal) => {
              const isSubscribedGoal = Me.hasActiveSubscriptionToGoal(
                me,
                goal.uid
              );
              return (
                <GoalCard
                  key={goal.uid}
                  goal={goal}
                  isMyGoal
                  isSubscribedGoal={isSubscribedGoal}
                  isFollowedGoal
                  removeGoalAction={removeFromMyGoals}
                  onClick={() => {
                    gotoSelectedGoal(goal);
                  }}
                />
              );
            })}
          </FollowedGoals>
          <Title color={theme.colors.fontGrey}>All goals</Title>
        </>
      )}
      {goalGroups ? (
        <>
          <GoalGroupNav
            goalGroups={goalGroups}
            setCurrentRefIndex={setCurrentRefIndex}
            currentRefIndex={currentRefIndex}
            handleChooseGoal={handleChooseGoal}
            $nomarginTop={!isOnboarding}
          />
          <GoalItems>
            {goalGroups.map((goalGroup, i) => {
              const isMyGoal = goalGroup.uid === 'my-goals';
              return (
                <GoalGroupContainer
                  id={`${goalGroup.uid}`}
                  className={goalGroup.name}
                  key={`item-${goalGroup.uid}`}
                  ref={goalGroupRefs.current[i]}
                >
                  <P1 color={theme.colors.fontGrey}>{goalGroup.name}</P1>
                  <GoalGroupChildren>
                    {goalGroup.children.map((goal) => {
                      const isSubscribedGoal = Me.hasActiveSubscriptionToGoal(
                        me,
                        goal.uid
                      );
                      const isFollowedGoal =
                        followedGoals.filter(
                          (followedGoal) => followedGoal.uid === goal.uid
                        ).length > 0;
                      return (
                        <GoalCard
                          key={`goal-${goal.uid}`}
                          goal={goal}
                          onClick={onGoalCardClick({
                            isMyGoal,
                            isSubscribedGoal,
                            isFollowedGoal
                          })}
                          removeGoalAction={removeFromMyGoals}
                          isMyGoal={isMyGoal}
                          isSubscribedGoal={isSubscribedGoal}
                          isFollowedGoal={isFollowedGoal}
                        />
                      );
                    })}
                  </GoalGroupChildren>
                </GoalGroupContainer>
              );
            })}
          </GoalItems>
          {drawerState && (
            <LanguageSelectionDrawer
              languages={goalLanguages}
              onLanguageSelect={onLanguageSelect}
              drawerState={drawerState}
              setShowLanguages={setdrawerState}
              hideCloseButton
              heading="Pick a goal Language"
              subHeading="Courses in the preferred language will be shown first"
            />
          )}
          <Dialog
            onClose={() => setGoalToBeRemoved(null)}
            dialogState={!!goalToBeRemoved}
            heading={`Remove ${goalToBeRemoved ? goalToBeRemoved.name : ''} ?`}
            message="You will stop receiving updates related to this goal"
            acceptLabel="Remove"
            rejectLabel="Cancel"
            acceptOnClick={removeGoalCall}
            rejectOnClick={() => setGoalToBeRemoved(null)}
          />
          <Dialog
            onClose={() => setGoalToBeRemoved(null)}
            dialogState={!!goalToBeRemoved}
            heading={`Remove ${goalToBeRemoved ? goalToBeRemoved.name : ''} ?`}
            message="You will stop receiving updates related to this goal"
            acceptLabel="Remove"
            rejectLabel="Cancel"
            acceptOnClick={removeGoalCall}
            rejectOnClick={() => setGoalToBeRemoved(null)}
          />
          <ChooseGoal
            chooseGoal={chooseGoal}
            resetChooseGoal={resetChooseGoal}
            me={me}
            meModal={Me}
            followedGoals={followedGoals}
            removeGoalAction={removeFromMyGoals}
            onGoalCardClick={onGoalCardClick}
          />
        </>
      ) : null}
    </Container>
  );
};

export default ManageGoals;

ManageGoals.propTypes = {
  goalGroupsData: PropTypes.arrayOf([PropTypes.any]),
  followedGoals: PropTypes.arrayOf([PropTypes.any]),
  isOnboarding: PropTypes.bool,
  goalLanguages: PropTypes.arrayOf([PropTypes.any])
};

ManageGoals.defaultProps = {
  goalGroupsData: [],
  followedGoals: [],
  isOnboarding: false,
  goalLanguages: []
};
